import { TRAINING } from '../core/SimConfig.js';

/**
 * Instructor voiceline queue + on-screen captions for training missions.
 *
 * Lines are queued and played strictly in order, one at a time, each held for
 * its own `duration` before the next takes over — so two objectives firing in
 * quick succession never talk over each other. The queue is driven from the sim
 * clock (`update(dt)`), which means it freezes with the game when paused (and
 * so does the voice, via {@link setPaused}).
 *
 * The briefing for whatever the player is doing *now* always outranks anything
 * still waiting, so {@link dropStale} bins the backlog whenever a new objective
 * takes over — otherwise a player who moves briskly would hear each step's
 * instructions two steps too late, or not at all. Short `sticky` lines (the
 * "nice work" after an objective clears) survive that cull.
 *
 * Lines are tagged with the id of the objective that queued them, which also
 * lets {@link isPending} answer "has the instructor finished this thought?" —
 * what an `info` objective waits on to complete.
 *
 * ── Voice ──────────────────────────────────────────────────────────────────
 * A whole {@link enqueue} call shares *one* recording: an objective's briefing
 * is a single file no matter how many caption lines it's broken into (hints and
 * completion credits are their own one-line groups, and so their own files).
 * That means the group, not the line, is the unit of playback:
 *
 *   • The file starts when the group's first line goes up, and keeps running
 *     underneath the rest of them.
 *   • Starting any other group cuts it — a new objective, a hint, or a credit
 *     line interrupts the instructor rather than queueing behind him, matching
 *     what {@link dropStale} does to the captions.
 *   • Once the file's real length is known the group's authored durations are
 *     rescaled to fit it, so the captions track the voice instead of drifting
 *     off it. Lines with no recording just use their authored durations.
 *
 * Audio is streamed through plain HTMLAudioElements (see `trainingVoiceUrl` in
 * audio/soundManifest.js for why) — one element per file, reused, since only
 * one voiceline is ever sounding at a time.
 */
export default class CaptionPlayer {
  constructor({ masterVolume = 1, voiceVolume = 1 } = {}) {
    const el = document.createElement('div');
    el.id = 'training-captions';
    this.el = el;
    document.getElementById('app').appendChild(el);

    /** @type {Array<{caption:string,duration:number,owner:string,sticky:boolean,group:object}>} */
    this._queue = [];
    this._current = null;
    this._timer = 0;   // time left on the current line, then the inter-line gap
    this._played = 0;  // time the current line has been up (for mid-line retiming)
    this._gap = 0;     // silence remaining before the next line may start

    this._voices = new Map();   // url → HTMLAudioElement
    this._audio = null;         // element currently sounding, if any
    this._group = null;         // group meta the sounding element belongs to
    this._voiceTime = 0;        // sim time the current recording has been running
    this._masterVolume = masterVolume;
    this._voiceVolume = voiceVolume;
    this._paused = false;
  }

  /**
   * True while a line is on screen, one is waiting to start, or the instructor
   * is still talking — a voiceline that outruns its captions still counts, so
   * hints don't fire over the tail of a briefing.
   */
  get isSpeaking() {
    if (this._current !== null || this._queue.length > 0) return true;
    return this._isSounding();
  }

  /**
   * Queue a set of lines, tagged with `owner` (the objective id that produced
   * them). `lines` accepts the authoring shape `{ caption, duration }`. The
   * whole call is one voice group: `audio` (a URL, or null) plays across all of
   * them. `sticky` lines are exempt from {@link dropStale}.
   */
  enqueue(lines, owner = '', { sticky = false, audio = null } = {}) {
    if (!lines) return;
    const entries = [];
    for (const line of lines) {
      if (!line || !line.caption) continue;
      entries.push({
        caption: line.caption,
        duration: Math.max(TRAINING.caption.minDuration, line.duration ?? 3),
        owner,
        sticky,
      });
    }
    if (!entries.length) return;

    // Shared per-group state. Held by reference off each line, so it dies with
    // them — the scale is filled in when the recording's length is known.
    const group = {
      url: audio,
      owner,
      authored: entries.reduce((sum, e) => sum + e.duration, 0),
      count: entries.length,
      scale: 1,
    };
    for (const e of entries) {
      e.group = group;
      this._queue.push(e);
    }
  }

  /** True while any line tagged `owner` is playing, still queued, or sounding. */
  isPending(owner) {
    if (this._current?.owner === owner) return true;
    if (this._group?.owner === owner && this._isSounding()) return true;
    return this._queue.some((l) => l.owner === owner);
  }

  /**
   * Bin every line that hasn't started yet — briefings and hints for steps the
   * player has already flown past — except sticky lines owned by `keepOwner`.
   * That exemption is what lets the credit for the objective just cleared still
   * land, while bounding the backlog to a single line no matter how fast the
   * player chains objectives. The line currently on screen is left to finish,
   * voice and all; whatever plays next cuts it off at that point.
   */
  dropStale(keepOwner = null) {
    for (let i = this._queue.length - 1; i >= 0; i--) {
      const line = this._queue[i];
      if (!line.sticky || line.owner !== keepOwner) this._queue.splice(i, 1);
    }
  }

  update(dt) {
    if (this._audio) this._voiceTime += dt;

    if (this._gap > 0) {
      this._gap -= dt;
      if (this._gap > 0) return;
    }

    if (this._current) {
      this._timer -= dt;
      this._played += dt;
      if (this._timer > 0) return;
      this._current = null;
      this.el.classList.remove('is-visible');
      // Only pay the inter-line gap when something is actually waiting, so the
      // caption doesn't hang on screen after the last line of a run.
      this._gap = this._queue.length ? TRAINING.caption.gap : 0;
      if (this._gap > 0) return;
    }

    if (this._queue.length) this._play(this._queue.shift());
  }

  _play(line) {
    const group = line.group;
    // First line of a new group: its recording takes over from whatever was
    // sounding, mid-sentence if need be.
    if (group !== this._group) this._startVoice(group);

    this._current = line;
    this._played = 0;
    this._timer = this._lineDuration(line);
    this.el.textContent = line.caption;
    this.el.classList.add('is-visible');
  }

  // ── Voice ──────────────────────────────────────────────────────────────────

  _startVoice(group) {
    this._stopVoice();
    this._group = group;
    this._voiceTime = 0;
    if (!group.url) return;

    const audio = this._voice(group.url);
    this._audio = audio;
    audio.volume = this._volume();
    try { audio.currentTime = 0; } catch { /* not yet seekable */ }
    if (!this._paused) audio.play().catch(() => { /* autoplay blocked — captions carry on */ });

    if (this._rescale(group, audio)) return;
    // Length not known yet (still loading): retime the group the moment it is,
    // as long as it's still the one being spoken.
    audio.addEventListener('loadedmetadata', () => {
      if (this._group !== group) return;
      if (this._rescale(group, audio) && this._current?.group === group) {
        this._timer = Math.max(0.2, this._lineDuration(this._current) - this._played);
      }
    }, { once: true });
  }

  // Fit the group's authored durations to the recording's real length: they're
  // written as estimates, so they're treated as relative weights and stretched
  // (or squeezed) to span the file, gaps and a short tail included.
  _rescale(group, audio) {
    const length = audio.duration;
    if (!Number.isFinite(length) || length <= 0) return false;
    group.length = length;
    const cfg = TRAINING.caption;
    const onScreen = length + cfg.tail - (group.count - 1) * cfg.gap;
    if (onScreen <= 0) return false;
    const scale = onScreen / group.authored;
    group.scale = Math.min(cfg.maxStretch, Math.max(1 / cfg.maxStretch, scale));
    return true;
  }

  _lineDuration(line) {
    return Math.max(TRAINING.caption.minDuration, line.duration * line.group.scale);
  }

  // One element per file, kept for the life of the mission. Voicelines never
  // overlap, so a file that plays twice (a repeated hint) just rewinds.
  _voice(url) {
    let audio = this._voices.get(url);
    if (!audio) {
      audio = new Audio(url);
      audio.preload = 'auto';
      this._voices.set(url, audio);
    }
    return audio;
  }

  /** Warm the cache (and the durations) for every line a mission can speak. */
  preload(urls) {
    for (const url of urls) if (url) this._voice(url).load();
  }

  /**
   * Is the instructor still talking? Measured on the sim clock against the
   * recording's known length rather than off the element's own `ended` flag: a
   * file that fails to load, stalls, or is silently dropped by the browser
   * would otherwise report "still speaking" forever, and an `info` objective
   * waits on exactly this to advance. A recording whose length never arrived
   * counts as not sounding, so the mission always moves on.
   */
  _isSounding() {
    return this._audio !== null && this._group?.length > 0
      && this._voiceTime < this._group.length;
  }

  _stopVoice() {
    if (this._audio) {
      this._audio.pause();
      try { this._audio.currentTime = 0; } catch { /* not yet seekable */ }
    }
    this._audio = null;
    this._group = null;
    this._voiceTime = 0;
  }

  _volume() {
    return Math.max(0, Math.min(1, TRAINING.caption.volume * this._voiceVolume * this._masterVolume));
  }

  /**
   * Freeze/resume the voice with the sim. The caption clock already stops with
   * the pause (it's fed sim time), so without this the instructor would keep
   * talking over the pause menu and come back out of sync with his captions.
   */
  setPaused(paused) {
    if (paused === this._paused) return;
    this._paused = paused;
    if (!this._audio) return;
    if (paused) this._audio.pause();
    else if (this._isSounding()) this._audio.play().catch(() => {});
  }

  /** User master-volume change from the settings overlay. */
  setMasterVolume(v) {
    this._masterVolume = Math.max(0, Math.min(1, v));
    if (this._audio) this._audio.volume = this._volume();
  }

  /** User voice-volume change from the settings overlay. */
  setVoiceVolume(v) {
    this._voiceVolume = Math.max(0, Math.min(1, v));
    if (this._audio) this._audio.volume = this._volume();
  }

  clear() {
    this._queue.length = 0;
    this._current = null;
    this._timer = 0;
    this._played = 0;
    this._gap = 0;
    this._stopVoice();
    this.el.classList.remove('is-visible');
  }

  dispose() {
    this.clear();
    for (const audio of this._voices.values()) audio.src = '';
    this._voices.clear();
    this.el.remove();
  }
}
